import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { socketService } from '../services/socketService';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: radial-gradient(circle at center, #2a8d2a 0%, #1a5d1a 50%, #0f3d0f 100%);
  padding: 20px;
`;

const SetupCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 28px;
  font-weight: 600;
`;

const PlayerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
`;

const InputLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
`;

const PlayerInput = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 16px;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`;

const PlayerNumber = styled.span`
  position: absolute;
  left: -30px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 500;
`;

const Button = styled.button<{ $isDisabled?: boolean }>`
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 12px;
  background: ${props => props.$isDisabled 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'linear-gradient(135deg, #27ae60, #219a52)'};
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.$isDisabled 
    ? 'none' 
    : '0 4px 15px rgba(39, 174, 96, 0.3)'};

  &:hover {
    transform: ${props => props.$isDisabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$isDisabled 
      ? 'none' 
      : '0 6px 20px rgba(39, 174, 96, 0.4)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  text-align: center;
  margin-top: 10px;
  font-size: 14px;
`;

const LeaderSetup = () => {
  const [playerNames, setPlayerNames] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (index: number, value: string) => {
    const newNames = [...playerNames];
    newNames[index] = value;
    setPlayerNames(newNames);
    setError('');
  };

  const handleSubmit = async () => {
    const validNames = playerNames.filter(name => name.trim() !== '');
    
    if (validNames.length < 2) {
      setError('Please enter at least 2 player names');
      return;
    }

    if (new Set(validNames).size !== validNames.length) {
      setError('Player names must be unique');
      return;
    }

    setIsSubmitting(true);
    try {
      await socketService.setupPlayers({
        leaderName: validNames[0],
        players: validNames
      });
      navigate('/waiting-room');
    } catch (err) {
      setError('Failed to setup players. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <SetupCard>
        <Title>Game Setup</Title>
        <PlayerList>
          {playerNames.map((name, index) => (
            <PlayerInput key={index}>
              <InputLabel>
                {index === 0 ? 'Leader Name (You)' : `Player ${index + 1}`}
              </InputLabel>
              <Input
                type="text"
                placeholder={index === 0 ? 'Enter your name' : `Enter player name`}
                value={name}
                onChange={(e) => handleInputChange(index, e.target.value)}
                maxLength={20}
              />
            </PlayerInput>
          ))}
        </PlayerList>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          $isDisabled={isSubmitting}
        >
          {isSubmitting ? 'Setting up...' : 'Finish Setup'}
        </Button>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SetupCard>
    </Container>
  );
};

export default LeaderSetup; 